import Lottie from 'lottie-react';
import { useMemo, type CSSProperties } from 'react';

interface LottieAnimationProps {
  /** Lottie animation data (JSON object) or path to JSON file */
  animationData?: object;
  /** URL to the Lottie JSON file */
  path?: string;
  /** Whether the animation should loop */
  loop?: boolean;
  /** Whether the animation should autoplay */
  autoplay?: boolean;

  /** CSS styles for the animation container */
  style?: CSSProperties;
  /** CSS classes for the animation container */
  className?: string;
  /** Width of the animation */
  width?: number | string;
  /** Height of the animation */
  height?: number | string;
  /** Callback when animation completes */
  onComplete?: () => void;
  /** Callback when animation loads */
  onLoopComplete?: () => void;
}

/**
 * LottieAnimation component for playing Lottie animations
 *
 * Usage examples:
 *
 * // With imported JSON data
 * import animationData from './animation.json';
 * <LottieAnimation animationData={animationData} width={200} height={200} />
 *
 * // With URL path
 * <LottieAnimation path="/animations/loading.json" loop autoplay />
 *
 * // With custom styling
 * <LottieAnimation
 *   animationData={data}
 *   className="w-32 h-32"
 *   onComplete={() => console.log('Animation completed!')}
 * />
 */
export function LottieAnimation({
  animationData,
  path,
  loop = true,
  autoplay = true,
  style,
  className,
  width,
  height,
  onComplete,
  onLoopComplete,
}: LottieAnimationProps) {
  const animationOptions = useMemo(
    () => ({
      animationData,
      path,
      loop,
      autoplay,
      style: {
        width,
        height,
        ...style,
      },
    }),
    [animationData, path, loop, autoplay, style, width, height],
  );

  return (
    <div className={className}>
      <Lottie
        {...animationOptions}
        onComplete={onComplete}
        onLoopComplete={onLoopComplete}
        style={animationOptions.style}
      />
    </div>
  );
}

export default LottieAnimation;
